<template>
    <div>
        <article class="ticket">
            <header class="ticket__wrapper">
                <div class="ticket__header" :class="statusClass">
                    {{ registrationCategory.name }}
                    <status-badge :human-status="registrationCategory.human_status">
                        <template #prefix
                                  v-if="registrationCategory.human_status === 'human_status_waiting_list' &&
                                  registrationCategory.show_participant_waiting_list_count"
                        >
                            <span v-if="registrationCategory.waiting_list_count > 1"><i class="fas fa-user-clock"></i> {{ registrationCategory.waiting_list_count }} people waiting</span>
                            <span v-else><i class="fas fa-user-clock"></i> {{ registrationCategory.waiting_list_count }} person waiting</span>
                        </template>

                    </status-badge>


                </div>
            </header>
            <div class="ticket__divider">
                <div class="ticket__notch"></div>
                <div class="ticket__notch ticket__notch--right"></div>
            </div>
            <div class="ticket__body" :class="!showFooter ? 'with-bottom' : ''">
                <section class="ticket__section">
                    <div class="row">

                        <div class="col-md-8 align-self-center mb-3 mb-md-0">
                            <!--Race Components -->
                            <race-components :components="registrationCategory.components"
                                             v-if="showRaceComponents"></race-components>
                            <p><strong>Date:</strong>
                                {{
                                    registrationCategory.date | dateFormat(registrationCategory.occasion_timezone, 'Do MMM YYYY')
                                }}
                                | <strong>Time:</strong>
                                {{ registrationCategory.date | timeFormat(registrationCategory.occasion_timezone) }}
                            </p>
                            <p v-if="!registrationCategory.no_closing_date && registrationCategory.closing_date !== null">
                                <strong>Closes In:</strong>
                                {{ registrationCategory.closing_date | daysHoursUntilDateTime }}</p>
                            <p v-if="registrationCategory.has_restrictions" style="font-style: italic">
                                {{ registrationCategory.entry_restrictions_text }}</p>
                        </div>
                        <div class="col-md-4 ml-auto align-self-center text-left text-md-right">
                            <price :price="registrationCategory.current_price">
                                <template #free><span class="price__free">£0.00</span></template>
                                <template #description>{{ registrationCategory.current_price.description }}</template>
                                <template #price-rise>
                                    <next-price-text :price="registrationCategory.next_price"
                                                     v-if="registrationCategory.next_price !== null"></next-price-text>
                                </template>
                            </price>
                            <div class="row mt-3 mt-md-0">
                                <div class="col-8 mx-auto ml-md-auto mr-md-0">
                                    <enter-btn :registration-category="registrationCategory"></enter-btn>
                                    <bulk-purchase-btn
                                        v-if="registrationCategory.pre_pay_setting !== null && !registrationCategory.hide_prepay_link"
                                        :human-status="registrationCategory.human_status">
                                    </bulk-purchase-btn>

                                </div>
                            </div>

                        </div>
                    </div>
                </section>
            </div>
            <div class="ticket__footer" v-if="showFooter">
                <Transition>
                    <span v-if="expand" v-html="registrationCategory.description"></span>
                </Transition>
                <a href="#" @click.prevent="expand = !expand"><span class="expander_icon"
                                                                    :class="expand ? 'expanded' : ''"></span>Read More..</a>
            </div>
        </article>
    </div>
</template>

<script>

import BulkPurchaseBtn from "./../Utility/BulkPurchaseBtn";
import RaceComponents from "./../Utility/RaceComponents";
import EnterBtn from "./../Utility/EnterBtn";
import NextPriceText from "../Utility/NextPriceText";
import Price from "./../Utility/Price";
import StatusBadge from "./../Utility/StatusBadge";
import {dateFormat, daysHoursUntilDateTime, timeFormat} from "../../../Utility/filters/dates"

export default {
    name: "Ticket",
    components: {
        RaceComponents,
        BulkPurchaseBtn,
        EnterBtn,
        NextPriceText,
        Price,
        StatusBadge
    },
    filters: {
        dateFormat,
        daysHoursUntilDateTime,
        timeFormat
    },
    props: {
        registrationCategory: {
            type: Object,
            required: true,
            default: () => {
            }
        }
    },
    computed: {
        statusClass() {
            switch (this.registrationCategory.human_status) {
                case 'human_status_open':
                    return 'open';

                case 'human_status_closed':
                case 'human_status_full':
                    return 'closed';

                case 'human_status_waiting_list':
                    return 'waiting-list';
            }
        },
        showFooter() {
            return this.registrationCategory.has_description;
        },
        showRaceComponents() {
            return this.registrationCategory.components !== undefined && this.registrationCategory.components !== null;
        }
    },
    data() {
        return {
            expand: false
        }
    }
}
</script>

<style scoped lang="scss">
$grey-light: hsl(200, 10%, 92%);
$grey: hsl(200, 10%, 85%);
$grey-dark: hsl(200, 10%, 70%);
$shadow: hsla(200, 20%, 20%, 0.25);

$open: #6fa551;
$closed: #dc143c;
$waitingList: #ecb618;

$bg: hsl(200, 0%, 100%);
$divider: $grey-light;
$border: $grey-light;

$cutout-size: 1rem;

.ticket {
    display: grid;
    grid-template-rows: auto 1fr auto;
    margin-bottom: 1em;

    &__header,
    &__body,
    &__footer {
        padding: 1rem 1.25rem;
        background-color: $bg;
        border: 1px solid $border;
        box-shadow: 0 2px 4px $shadow;
    }

    &__header {
        font-size: 1rem;

        border-bottom: none;
        box-shadow: none;
        padding: 0.8rem 1.25rem 0.5rem;

        font-weight: bold;
        display: flex;
        justify-content: space-between;

        &.open {
            border-top: 0.25rem solid $open;
        }

        &.closed {
            border-top: 0.25rem solid $closed;
        }

        &.waiting-list {
            border-top: 0.25rem solid $waitingList;
        }
    }

    &__wrapper {
        box-shadow: 0 2px 4px $shadow;
        border-radius: 0.375em 0.375em 0 0;
        overflow: hidden;
    }

    &__divider {
        position: relative;
        height: $cutout-size;
        background-color: $bg;
        margin-left: ($cutout-size / 2);
        margin-right: ($cutout-size / 2);

        &::after {
            content: '';
            position: absolute;
            height: 50%;
            width: 100%;
            top: 0;
            border-bottom: 2px dashed $divider;
        }
    }

    &__notch {
        position: absolute;
        left: ($cutout-size / 2) * -1;
        width: $cutout-size;
        height: $cutout-size;
        overflow: hidden;

        &::after {
            content: '';
            position: relative;
            display: block;
            width: ($cutout-size * 2);
            height: ($cutout-size * 2);
            right: 100%;
            top: -50%;
            border: ($cutout-size / 2) solid $bg;
            border-radius: 50%;
            box-shadow: inset 0 2px 4px $shadow;
        }

        &--right {
            left: auto;
            right: ($cutout-size / 2) * -1;

            &::after {
                right: 0;
            }
        }
    }

    &__body {
        border-bottom: none;
        border-top: none;

        & > * + * {
            margin-top: 1.5rem;
            padding-top: 1.5rem;
            border-top: 1px solid $divider;
        }
    }

    &__section {
        & > * + * {
            margin-top: 0.25rem;
        }

        & > h3 {
            font-size: 1.125rem;
            margin-bottom: 0.5rem;
        }

        & p {
            font-size: 0.9em;
            line-height: 1.4em;
        }
    }

    &__header,
    &__footer {

    }

    &__footer {
        font-size: 0.8rem;
        padding: 0.5rem 1.25rem;
        border-top: 2px dashed $divider;
        border-radius: 0 0 0.325rem 0.325rem;
        line-height: 1.4em;

        a {
            font-awesome: 0.9rem;
            color: #616363;
        }
    }

    .with-bottom {
        border-radius: 0 0 0.325rem 0.325rem;
    }
}

p {
    margin-top: 0;
    margin-bottom: 0.3rem;
}

.expander_icon {
    &:before {
        display: inline-block;
        font-style: normal;
        font-variant: normal;
        text-rendering: auto;
        -webkit-font-smoothing: antialiased;
        padding-right: 5px;
        font-family: "Font Awesome 5 Free";
        font-weight: 900;
        content: "\f107";
    }

    &.expanded {
        &:before {
            content: "\f106";
        }
    }
}

</style>



// WEBPACK FOOTER //
// src/Assets/components/RegistrationCategories/Tickets/Ticket/Ticket.vue